"use client";

import * as React from "react";
import { Command } from "cmdk";
import { Search } from "lucide-react";
import { cn } from "../primitives/cn";

export type CommandItem = {
  id: string;
  label: string;
  hint?: string;
  /** Route path for first-batch navigation-only commands */
  href: string;
  group?: string;
};

/**
 * CommandPalette — Cmd+K. First batch: route navigation only (search backend is phase 2).
 */
export function CommandPalette({
  open,
  onOpenChange,
  items,
  onSelect,
  labels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandItem[];
  onSelect: (item: CommandItem) => void;
  labels?: {
    defaultGroup?: string;
    placeholder?: string;
    empty?: string;
    closeAria?: string;
  };
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const defaultGroup = labels?.defaultGroup ?? "Navigation";
  const placeholder = labels?.placeholder ?? "Go to…";
  const empty = labels?.empty ?? "No matching commands";
  const closeAria = labels?.closeAria ?? "Close command palette";

  const groups = React.useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const item of items) {
      const g = item.group ?? defaultGroup;
      const list = map.get(g) ?? [];
      list.push(item);
      map.set(g, list);
    }
    return map;
  }, [items, defaultGroup]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 pt-[15vh] p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label={closeAria}
        onClick={() => onOpenChange(false)}
      />
      <Command
        className={cn(
          "relative z-10 w-full max-w-lg overflow-hidden rounded-lg border border-border bg-card shadow-lg animate-fade-in",
        )}
        label="Command palette"
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="size-4 text-muted-foreground" />
          <Command.Input
            placeholder={placeholder}
            className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <Command.List className="max-h-72 overflow-y-auto p-2">
          <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
            {empty}
          </Command.Empty>
          {[...groups.entries()].map(([group, groupItems]) => (
            <Command.Group key={group} heading={group} className="text-xs text-muted-foreground">
              {groupItems.map((item) => (
                <Command.Item
                  key={item.id}
                  value={`${item.label} ${item.hint ?? ""} ${item.href}`}
                  onSelect={() => {
                    onSelect(item);
                    onOpenChange(false);
                  }}
                  className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm text-foreground aria-selected:bg-accent"
                >
                  <span>{item.label}</span>
                  {item.hint ? (
                    <span className="text-xs text-muted-foreground">{item.hint}</span>
                  ) : null}
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command>
    </div>
  );
}
